import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, CircularProgress } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ICandidate, newMappingEntity, uploadFileToS3 } from 'wisestep-api';
import {
  PersonalInfo,
  LocationInfo,
  WorkExpForm,
  JobPreference,
  SalaryDetails,
  AssessmentInfo,
  DocumentInfo,
  Item,
  WsButton,
  WsColors,
} from 'wisestep-base';

import { warning } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../../../utils/useConversationWidgetStore';
import ActionFooter from '../../../../conversationWidget/components/ActionFooter';
import { submissionSchema } from '../../../../conversationWidget/schema/formSchema';
import IOtherDocument from '../../../../conversationWidget/types/IOtherDocument';
import IResume from '../../../../conversationWidget/types/IResume';
import { trackCV, downloadCV } from '../../../../conversationWidget/utils/cvHelper';
import { submissionFormDefaultValues } from '../../../../conversationWidget/utils/fieldDefaultValues';
import { buildVMSSubmissionEntity } from '../../../../conversationWidget/utils/saveSubmission';
import { useSubmissionSettingContext } from '../../context/SubmissionSettingContext';

interface IViewVMSDetails {
  candidateDetail: ICandidate;
  afterReview: (candidate: ICandidate) => void;
}

const ViewVMSDetails = ({ candidateDetail, afterReview }: IViewVMSDetails) => {
  const [allowReset, setAllowReset] = useState<boolean>(false);
  const [resume, setResume] = useState<IResume | null>(null);
  const [otherDocument, setOtherDocument] = useState<IOtherDocument[]>([]);
  const submissionSetting = useSubmissionSettingContext();
  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [candidate, setCandidate] = useState(candidateDetail);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const formOptions = {
    defaultValues: submissionFormDefaultValues(candidate),
    resolver: yupResolver(submissionSchema(submissionSetting)),
  };
  const methods = useForm(formOptions);
  const { watch, reset, handleSubmit } = methods;

  useEffect(() => {
    candidate && allowReset && reset(submissionFormDefaultValues(candidate));
  }, [allowReset, candidate, reset]);

  const onFormSubmit = async (data: FieldValues) => {
    setDisableSubmit(true);
    if (isEmpty(candidate?.resumeLocation)) {
      submit(data);
      setNotification(warning('Please upload CV'));
      setDisableSubmit(false);
    } else {
      submit(data);
    }
  };
  const update = (data: FieldValues, conversation: ICandidate) =>
    afterReview(buildVMSSubmissionEntity(data, conversation, otherDocument, resume));

  const resumeAction = (action: string, url: string | null) => {
    switch (action) {
      case 'view':
        !isEmpty(url) && trackCV('view', url, cvViewSourcePage);
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, cvViewSourcePage);
          url && downloadCV(url);
        }
        break;
      case 'removeCandidate':
        if (candidate) {
          const alteredCandidate = { ...candidate };
          alteredCandidate.resumeFileOriginalName = '';
          alteredCandidate.resumeLocation = '';
          alteredCandidate.resumeUploadedOn = null;
          setCandidate({ ...alteredCandidate });
        }
        break;
      case 'removeOtherDocument':
        if (candidate) {
          const alteredCandidate = { ...candidate };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          setCandidate({ ...alteredCandidate });
        }
        break;
    }
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'document') {
      const alteredCandidate = candidate ?? newMappingEntity();
      const alteredDocuments = [...otherDocument];
      alteredDocuments.push({
        documentLocation: url,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
      setAllowReset(false);
      alteredCandidate.otherDocuments = alteredDocuments;
      setCandidate({ ...alteredCandidate });
    } else if (field === 'resume') {
      const alteredCandidate = candidate ?? newMappingEntity();
      alteredCandidate.resumeLocation = url;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      setResume({
        resumeLocation: url,
        resumeFileOriginalName: file[0].name,
        resumeUploadedOn: new Date(),
      });
      const res = {
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        resumeFileOriginalName: file[0].name,
        resumeLocation: url,
        resumeUploadedOn: new Date(),
      };
      setAllowReset(false);
      alteredCandidate.mappedCandidateResumes = [res];
      setCandidate({ ...alteredCandidate });
    }
  };

  const submit = async (data: FieldValues) => {
    if (candidate && candidate.isJobCandidateMapping === false) {
      update(data, candidate);
    }
  };

  const onError = (e) => {
    console.log(e);
    setNotification(warning('Please enter required fields'));
  };

  return (
    <FormProvider {...methods}>
      {!disableSubmit ? (
        <Box component="form" onSubmit={handleSubmit(onFormSubmit, onError)}>
          <Grid item xs={12} sx={{ maxHeight: '100vh', overflowY: 'auto' }}>
            <PersonalInfo
              candidate={candidate}
              onUpload={(file: File[]) => onUpload(file, 'resume')}
              submissionSetting={submissionSetting}
              resumeAction={resumeAction}
            />
            <LocationInfo submissionSetting={submissionSetting} />
            <WorkExpForm submissionSetting={submissionSetting} />
            <JobPreference watch={watch} submissionSetting={submissionSetting} />
            <SalaryDetails submissionSetting={submissionSetting} />
            <AssessmentInfo submissionSetting={submissionSetting} />
            <DocumentInfo
              candidate={candidate}
              submissionSetting={submissionSetting}
              onUpload={(file: File[]) => onUpload(file, 'document')}
              resumeAction={resumeAction}
            />
            <Item sx={{ mb: 12 }}></Item>
            <ActionFooter>
              <Grid item container xs={6} justifyContent="flex-end">
                <Box mr={3}>
                  <WsButton variant="outlined" type="submit" disabled={disableSubmit}>
                    Next
                  </WsButton>
                </Box>
              </Grid>
            </ActionFooter>
          </Grid>
        </Box>
      ) : (
        <Box textAlign="center" mt={40}>
          <CircularProgress
            disableShrink
            sx={{
              color: WsColors.TheBlues500,
              height: '80px !important',
              width: '80px !important',
            }}
          />
        </Box>
      )}
    </FormProvider>
  );
};

export default ViewVMSDetails;
